import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BG_OPTIONS } from "@/pages/Waitlist";
import { ArrowLeft, UploadCloud, Loader2 } from "lucide-react";
import Logo from "@/components/Logo";

export default function Apply() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [background, setBackground] = useState("");
  const [experience, setExperience] = useState("");
  const [resume, setResume] = useState(null);
  const [supporting, setSupporting] = useState([]);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [bio, setBio] = useState("");
  const [workplace, setWorkplace] = useState("");
  const [degree, setDegree] = useState("");
  const [fieldOfStudy, setFieldOfStudy] = useState("");
  const [profExperience, setProfExperience] = useState("");
  const [relevantExp, setRelevantExp] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [accuracy, setAccuracy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [future, setFuture] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/apply";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    if (user.email && !email) setEmail(user.email);
    if (user.name && !fullName) setFullName(user.name);
  }, [loading, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!resume) { toast.error("Please upload your resume (PDF, DOC or DOCX)."); return; }
    const ext = resume.name.toLowerCase();
    if (!(ext.endsWith(".pdf") || ext.endsWith(".doc") || ext.endsWith(".docx"))) { toast.error("Unsupported file. Please upload PDF, DOC or DOCX."); return; }
    if (!fullName.trim()) { toast.error("Please enter your full name."); return; }
    if (!email.trim() || !email.includes("@")) { toast.error("Please enter a valid email address."); return; }
    if (phone.trim().length < 6) { toast.error("Please enter a valid phone number."); return; }
    if (!background) { toast.error("Please select your Bengali language background."); return; }
    if (!accuracy || !terms) { toast.error("Both required declarations must be accepted before submitting."); return; }

    const fd = new FormData();
    fd.append("resume", resume);
    fd.append("full_name", fullName);
    fd.append("email", email);
    fd.append("phone", phone);
    fd.append("linkedin", linkedin);
    fd.append("bio", bio);
    fd.append("current_workplace", workplace);
    fd.append("highest_degree", degree);
    fd.append("field_of_study", fieldOfStudy);
    fd.append("experience_years", profExperience);
    fd.append("relevant_experience", relevantExp);
    fd.append("bengali_background", background);
    fd.append("bengali_experience", experience);
    fd.append("accuracy_declared", accuracy ? "true" : "false");
    fd.append("terms_accepted", terms ? "true" : "false");
    fd.append("future_consent", future ? "true" : "false");
    supporting.forEach((f) => fd.append("supporting_documents", f));
    setSaving(true);
    try {
      await api.post("/application", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Application submitted.");
      navigate("/dashboard");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit your application."); }
    finally { setSaving(false); }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center bg-[#F6F3EC]"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#F6F3EC]">
      <header className="w-full border-b border-[#E5E5E5] bg-white">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <button data-testid="back-dash-btn" onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm font-medium text-[#525252] hover:text-[#0A0A0A]"><ArrowLeft className="h-4 w-4" /> Dashboard</button>
          <Logo variant="dark" to={null} />
        </div>
      </header>
      <div className="max-w-2xl mx-auto px-6 py-12">
        <span className="inline-flex items-center gap-2 text-[10px] font-medium uppercase tracking-wider bg-[#0A0A0A] text-white px-2 py-1 rounded-sm">Bengali Linguist Cohort</span>
        <h1 className="mt-4 font-display text-3xl font-bold tracking-tight">Complete your application</h1>
        <p className="mt-2 text-sm text-[#525252]">We are currently onboarding Bengali linguistic experts. Submitting an application does not guarantee acceptance, a project, employment or income.</p>

        <div className="mt-8 space-y-8 bg-white border border-[#E5E5E5] rounded-sm p-6 sm:p-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#0A0A0A] mb-3 pb-2 border-b border-[#E5E5E5]">Resume</p>
            <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Resume (PDF, DOC or DOCX) <span className="text-red-500">*</span></label>
            <label className="flex items-center gap-3 border-2 border-dashed border-[#E5E5E5] rounded-sm p-4 cursor-pointer hover:bg-[#F5F5F5]">
              <UploadCloud className="h-5 w-5 text-[#525252]" />
              <span className="text-sm text-[#525252]">{resume ? resume.name : "Click to upload your resume"}</span>
              <input data-testid="resume-input" type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setResume(e.target.files?.[0] || null)} />
            </label>
            {resume && (
              <button data-testid="autofill-btn" type="button" onClick={async () => {
                setExtracting(true);
                try {
                  const f = new FormData(); f.append("file", resume);
                  const r = await api.post("/resume/extract", f, { headers: { "Content-Type": "multipart/form-data" } });
                  const ex = r.data.extracted || {};
                  const status = r.data.extraction_status;
                  if (ex.full_name) setFullName(ex.full_name);
                  if (ex.email) setEmail(ex.email);
                  if (ex.phone) setPhone(ex.phone);
                  if (ex.linkedin) setLinkedin(ex.linkedin);
                  if (ex.bio) setBio(ex.bio);
                  if (ex.current_workplace) setWorkplace(ex.current_workplace);
                  if (ex.highest_degree) setDegree(ex.highest_degree);
                  if (ex.field_of_study) setFieldOfStudy(ex.field_of_study);
                  if (ex.experience_years) setProfExperience(ex.experience_years);
                  if (ex.relevant_experience) setRelevantExp(ex.relevant_experience);
                  if (status === "extracted") {
                    toast.success("We pre-filled what we could from your resume. Please review and complete the rest.");
                  } else {
                    toast.message("We couldn't read details from this file automatically. Please fill the fields manually.");
                  }
                } catch { toast.error("Could not read the resume. Please fill the fields manually."); }
                finally { setExtracting(false); }
              }} className="mt-2 inline-flex items-center gap-2 text-sm font-medium underline disabled:opacity-60" disabled={extracting}>
                {extracting && <Loader2 className="h-4 w-4 animate-spin" />} Autofill from résumé
              </button>
            )}
          </div>

          <div className="pt-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#0A0A0A] mb-3 pb-2 border-b border-[#E5E5E5]">Profile Information</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Full name <span className="text-red-500">*</span></label>
                <input data-testid="fullname-input" value={fullName} onChange={(e) => setFullName(e.target.value)} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Email <span className="text-red-500">*</span></label>
                <input data-testid="email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Phone number <span className="text-red-500">*</span></label>
                <input data-testid="phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91…" className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">LinkedIn <span className="text-[#A3A3A3] font-normal">(optional)</span></label>
                <input data-testid="linkedin-input" value={linkedin} onChange={(e) => setLinkedin(e.target.value)} placeholder="linkedin.com/in/…" className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Current workplace <span className="text-[#A3A3A3] font-normal">(optional)</span></label>
                <input data-testid="workplace-input" value={workplace} onChange={(e) => setWorkplace(e.target.value)} placeholder="Organisation name" className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Short bio <span className="text-[#A3A3A3] font-normal">(optional)</span></label>
              <textarea data-testid="bio-input" rows={3} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="A 1-2 sentence professional summary." className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-black" />
            </div>
            <p className="text-xs text-[#A3A3A3] mt-3">Signed in as {user.email}</p>
          </div>

          <div className="pt-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#0A0A0A] mb-3 pb-2 border-b border-[#E5E5E5]">Education &amp; Experience <span className="text-[#A3A3A3] font-normal normal-case">(optional)</span></p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Degree</label>
                <input data-testid="degree-input" value={degree} onChange={(e) => setDegree(e.target.value)} placeholder="e.g. B.A., M.A." className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Field / Area of study</label>
                <input data-testid="field-input" value={fieldOfStudy} onChange={(e) => setFieldOfStudy(e.target.value)} placeholder="e.g. Linguistics" className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Professional experience</label>
                <input data-testid="prof-experience-input" value={profExperience} onChange={(e) => setProfExperience(e.target.value)} placeholder="e.g. 5 years" className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Relevant professional / domain experience</label>
              <textarea data-testid="relevant-experience-input" rows={3} value={relevantExp} onChange={(e) => setRelevantExp(e.target.value)} placeholder="A short summary of relevant professional or domain experience." className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-black" />
            </div>
          </div>

          <div className="pt-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#0A0A0A] mb-3 pb-2 border-b border-[#E5E5E5]">Bengali Language Background</p>
            <label className="block text-sm font-medium text-[#0A0A0A] mb-2">What best describes your Bengali language background? <span className="text-red-500">*</span></label>
            <div className="space-y-2">
              {BG_OPTIONS.map((o) => (
                <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input data-testid={`bg-${o.slice(0,10)}`} type="radio" name="bg" checked={background === o} onChange={() => setBackground(o)} className="accent-[#0A0A0A]" /> {o}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Please describe your relevant Bengali linguistic experience. <span className="text-[#A3A3A3] font-normal">(optional)</span></label>
            <p className="text-xs text-[#A3A3A3] mb-2">e.g. translation, writing, editing, proofreading, teaching, linguistics, literature, journalism, research, voice/script work, localisation, academic work.</p>
            <textarea data-testid="experience-input" rows={5} value={experience} onChange={(e) => setExperience(e.target.value)} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-black" />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Supporting documents (optional)</label>
            <input data-testid="supporting-input" type="file" multiple accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={(e) => setSupporting(Array.from(e.target.files || []))} className="text-sm" />
            <p className="text-xs text-[#A3A3A3] mt-1">Certificates, work samples, academic or professional evidence.</p>
          </div>

          <div className="space-y-3 border-t border-[#E5E5E5] pt-6">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input data-testid="chk-accuracy" type="checkbox" checked={accuracy} onChange={(e) => setAccuracy(e.target.checked)} className="mt-1 accent-[#0A0A0A]" />
              <span>I confirm that, to the best of my knowledge, the information I have provided is accurate and that documents submitted by me are genuine.</span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input data-testid="chk-terms" type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1 accent-[#0A0A0A]" />
              <span>I have read and agree to the <Link to="/terms" target="_blank" className="underline">Terms of Use</Link> and <Link to="/privacy" target="_blank" className="underline">Privacy Policy</Link>.</span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input data-testid="chk-future" type="checkbox" checked={future} onChange={(e) => setFuture(e.target.checked)} className="mt-1 accent-[#0A0A0A]" />
              <span>(Optional) I would like FIAD to retain my profile for consideration for relevant future cohorts or opportunities.</span>
            </label>
          </div>

          <button data-testid="submit-application-btn" onClick={submit} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-white px-6 py-3 rounded-sm text-sm font-medium hover:bg-[#262626] disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}{saving ? "Submitting…" : "Submit Application"}
          </button>
        </div>
      </div>
    </div>
  );
}
